import {
  Component,
  ComponentInterface,
  h,
  Prop,
  State,
  Watch,
} from "@stencil/core";
import { DeviceService, ThemeService } from "../../../services";
import { IThemeOptions } from "../../../models";

@Component({
  tag: "av-card",
  styleUrl: "av-card.scss",
  // shadow: true,
})
export class AvCard implements ComponentInterface {
  @Prop() open: boolean;
  @Prop() focused: boolean = true;
  @Prop() inline: boolean = false;
  @Prop() ignoreTheme: boolean = false;
  @Prop() resizable: boolean = false;
  @Prop() resizeRatio: number;
  @Prop() positionHorizontal: "left" | "right" | "center";

  @State() themeOptions: IThemeOptions;
  @State() resizing = false;

  @Watch("open")
  openChange(value: boolean) {
    // reset size on close
    if (value === false && !!this.resizeContainer) {
      this.resizeContainer.style.removeProperty("width");
      this.resizeContainer.style.removeProperty("height");
    }
  }

  supportsBackdropBlur = true;

  constructor() {
    ThemeService.optionsAvailable().subscribe(
      (options) => (this.themeOptions = options)
    );
    this.supportsBackdropBlur = !DeviceService.isFirefox;
  }

  private resizeContainer: HTMLDivElement;
  // private resizeLamda: { x: number; y: number };
  private resizeRect: { width: number; height: number };
  private originalMousePosition: { x: number; y: number };
  private originalElemSize: { width: number; height: number };

  private resizeStart(event) {
    event.preventDefault();
    this.resizing = true;

    this.originalElemSize = {
      width: this.resizeContainer.getBoundingClientRect().width,
      height: this.resizeContainer.getBoundingClientRect().height,
    };
    this.originalMousePosition = {
      x: event.clientX,
      y: event.clientY,
    };

    window.addEventListener("mousemove", this.resizeMove.bind(this));
    window.addEventListener("touchmove", this.resizeMove.bind(this));
    window.addEventListener("mouseup", this.resizeEnd.bind(this));
    window.addEventListener("touchEnd", this.resizeEnd.bind(this));

    document.body.style.setProperty("pointer-events", "none");
  }

  // @Listen('mousemove', { target: 'window' })
  resizeMove(event: MouseEvent) {
    if (!this.originalElemSize || !event.isTrusted) {
      return;
    }

    const x = event.clientX;
    const y = event.clientY;

    // NW
    if (this.isPositionBottom && this.isPositionRight) {
      const width =
        this.originalElemSize.width - (x - this.originalMousePosition.x);
      this.resizeRect = {
        width,
        height: !!this.resizeRatio
          ? width / this.resizeRatio
          : this.originalElemSize.height - (y - this.originalMousePosition.y),
      };
    }
    // NE
    else if (
      this.isPositionBottom &&
      (this.isPositionLeft || this.isPositionCenter)
    ) {
      const width =
        this.originalElemSize.width + (x - this.originalMousePosition.x);
      this.resizeRect = {
        width,
        height: !!this.resizeRatio
          ? width / this.resizeRatio
          : this.originalElemSize.height - (y - this.originalMousePosition.y),
      };
    }
    // SW
    else if (this.isPositionTop && this.isPositionRight) {
      const width =
        this.originalElemSize.width - (x - this.originalMousePosition.x);
      this.resizeRect = {
        width,
        height: !!this.resizeRatio
          ? width / this.resizeRatio
          : this.originalElemSize.height + (y - this.originalMousePosition.y),
      };
    }
    // SE
    else if (
      this.isPositionTop &&
      (this.isPositionLeft || this.isPositionCenter)
    ) {
      const width =
        this.originalElemSize.width + (x - this.originalMousePosition.x);
      this.resizeRect = {
        width,
        height: !!this.resizeRatio
          ? width / this.resizeRatio
          : this.originalElemSize.height + (y - this.originalMousePosition.y),
      };
    }

    const maxRect = {
      width: window.innerWidth - 60,
      height: window.innerHeight - 140,
    };
    this.resizeContainer.style.setProperty(
      "width",
      `${
        !this.resizeRatio && this.resizeRect.width < 320
          ? 320
          : this.resizeRect.width > maxRect.width
          ? maxRect.width
          : this.resizeRect.width
      }px`
    );
    this.resizeContainer.style.setProperty(
      "height",
      `${
        !this.resizeRatio && this.resizeRect.height < 240
          ? 240
          : this.resizeRect.height > maxRect.height
          ? maxRect.height
          : this.resizeRect.height
      }px`
    );

    // this.resizeContainer.style.width = `${this.resizeRect.width}px`;
    // this.resizeContainer.style.height = `${this.resizeRect.height}px`;
    // Log.info('resize move', this.resizeRect, this.resizeLamda);
  }

  // @Listen('mouseup', { target: 'window' })
  resizeEnd() {
    this.resizing = false;
    this.resizeRect = null;
    this.originalElemSize = null;
    this.resizeContainer.style.transition = null;
    window.removeEventListener("mousemove", this.resizeMove.bind(this));
    window.removeEventListener("touchmove", this.resizeMove.bind(this));
    window.removeEventListener("mouseup", this.resizeEnd.bind(this));
    window.removeEventListener("touchEnd", this.resizeEnd.bind(this));
    document.body.style.setProperty("pointer-events", null);
  }

  get isPositionLeft() {
    return this.themeOptions.positionHorizontal === "left";
  }
  get isPositionRight() {
    return this.themeOptions.positionHorizontal === "right";
  }
  get isPositionCenter() {
    return this.themeOptions.positionHorizontal === "center";
  }
  get isPositionTop() {
    return this.themeOptions.positionVertical === "top";
  }
  get isPositionBottom() {
    return this.themeOptions.positionVertical === "bottom";
  }

  render() {
    return (
      <div
        ref={(el) => (this.resizeContainer = el)}
        class={{
          "av-card": true,
          "av-card-floating": !this.inline,
          "av-card-dark": !this.ignoreTheme && this.themeOptions.darkMode,
          "av-card-backdrop-blur":
            !this.ignoreTheme && this.supportsBackdropBlur,
          "av-card-open": this.open,
          "av-card-closed": !this.open,
          "av-card-focus": this.focused,
          "av-card-blur": !this.focused,
          "av-card-resizing": this.resizing,
          "av-card-right": this.isPositionRight,
          "av-card-center": this.isPositionCenter,
          "av-card-left": this.isPositionLeft,
          "av-card-top": this.isPositionTop,
          "av-card-bottom": this.isPositionBottom,
          "av-card-mobile": DeviceService.isMobile,
        }}
      >
        {this.resizable && (
          <div
            class={{
              "av-card-tools": true,
              "av-card-tools-top": this.isPositionTop,
              "av-card-tools-bottom": this.isPositionBottom,
              "av-card-tools-left": this.isPositionLeft,
              "av-card-tools-right": this.isPositionRight,
              "av-card-tools-center": this.isPositionCenter,
            }}
          >
            <button
              class={{
                "tool tool-resize": true,
                "tool-top-left": this.isPositionBottom && this.isPositionRight,
                "tool-top-right": this.isPositionBottom && this.isPositionLeft,
                "tool-bottom-left": this.isPositionTop && this.isPositionRight,
                "tool-bottom-right": this.isPositionTop && this.isPositionLeft,
                "tool-top-center":
                  this.isPositionBottom && this.isPositionCenter,
                "tool-bottom-center":
                  this.isPositionTop && this.isPositionCenter,
              }}
              title="resize"
              onTouchStart={this.resizeStart.bind(this)}
              onMouseDown={this.resizeStart.bind(this)}
            >
              <av-asset color="light" image="arrow-top-left"></av-asset>
            </button>
          </div>
        )}
        <slot></slot>
        <div class="av-card-body">
          <slot name="card-body"></slot>
        </div>
        <div class="av-card-footer">
          <slot name="card-footer"></slot>
        </div>
      </div>
    );
  }
}
